import React, { createContext, useContext, useEffect, useState } from "react";
import { api, errorMessage, setAuthTokens, clearAuthTokens } from "./api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=logged
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cart") || "[]"); } catch { return []; }
  });

  const [wishlist, setWishlist] = useState(() => new Set());

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  // Wishlist product ids for the heart icons on product cards
  useEffect(() => {
    if (!user) { setWishlist(new Set()); return; }
    api.get("/wishlist").then((r) => setWishlist(new Set((r.data || []).map((p) => p.id)))).catch(() => {});
  }, [user]);

  /** Returns false when the visitor must sign in first. */
  const toggleWishlist = async (product) => {
    if (!user) return false;
    const has = wishlist.has(product.id);
    setWishlist((prev) => { const n = new Set(prev); has ? n.delete(product.id) : n.add(product.id); return n; });
    try {
      if (has) { await api.delete(`/wishlist/${product.id}`); toast("Removed from wishlist"); }
      else { await api.post(`/wishlist/${product.id}`); toast.success("Saved to wishlist"); }
    } catch (e) {
      setWishlist((prev) => { const n = new Set(prev); has ? n.add(product.id) : n.delete(product.id); return n; });
      toast.error(errorMessage(e));
    }
    return true;
  };

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  const login = async (email, password) => {
    try {
      const r = await api.post("/auth/login", { email: email.trim(), password });
      const { access_token, refresh_token, ...u } = r.data;
      setAuthTokens(access_token, refresh_token);
      setUser(u);
      toast.success(`Welcome back${u.name ? `, ${u.name.split(" ")[0]}` : ""}`);
      return u;
    } catch (e) {
      toast.error(errorMessage(e, "Login failed"));
      throw e;
    }
  };
  const register = async (email, password, name) => {
    try {
      const r = await api.post("/auth/register", { email: email.trim(), password, name: name.trim() });
      const { access_token, refresh_token, ...u } = r.data;
      setAuthTokens(access_token, refresh_token);
      setUser(u);
      toast.success("Account created");
      return u;
    } catch (e) {
      toast.error(errorMessage(e, "Sign up failed"));
      throw e;
    }
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    clearAuthTokens();
    setUser(false);
    setWishlist(new Set());
    toast("Signed out");
  };
  // the bag can never hold more pieces than are in stock
  const addToCart = (product, quantity = 1) => {
    const stock = typeof product.stock === "number" ? product.stock : null;
    const inBag = (cart.find((c) => c.product_id === product.id) || {}).quantity || 0;
    if (stock !== null && inBag + quantity > stock) {
      toast.error(stock === 0 ? `${product.name} is sold out` : `Only ${stock} piece${stock > 1 ? "s" : ""} of ${product.name} in stock${inBag ? " — already in your bag" : ""}`);
      return;
    }
    setCart((prev) => {
      const exist = prev.find((c) => c.product_id === product.id);
      if (exist) return prev.map((c) => c.product_id === product.id ? { ...c, quantity: c.quantity + quantity, stock: stock ?? c.stock } : c);
      return [...prev, { product_id: product.id, name: product.name, price: product.price, image: (product.images || [])[0], slug: product.slug, quantity, stock }];
    });
    toast.success(`${product.name} added to bag`);
  };
  const removeFromCart = (pid) => setCart((p) => p.filter((c) => c.product_id !== pid));
  const updateQty = (pid, q) => setCart((p) => p.map((c) => {
    if (c.product_id !== pid) return c;
    const max = typeof c.stock === "number" && c.stock > 0 ? c.stock : 20;
    if (q > max) toast.error(`Only ${max} in stock`);
    return { ...c, quantity: Math.min(max, Math.max(1, q)) };
  }));
  const clearCart = () => setCart([]);

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, wishlist, toggleWishlist, cart, addToCart, removeFromCart, updateQty, clearCart }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
